import { differenceInCalendarDays, parseISO } from 'date-fns'
import { Calendar as CalendarIcon, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useEmployees } from '@/hooks'
import type { AbsenceTypeRecord } from '@/libs/interfaces'
import { cn } from '@/libs/utils'
import { useAppSelector } from '@/store/hooks'

const STANDARD_WORKDAY_HOURS = 8

export interface AbsenceDrawerSaveParams {
  absenceTypeID: string
  employeeId: string
  storeID: string
  start: string
  end: string
  durationMinutes: number
  comment: string
  partial: boolean
  startTime?: string
  endTime?: string
}

interface RegisterAbsenceDrawerProps {
  orgID: string
  onClose: () => void
  onSave: (params: AbsenceDrawerSaveParams) => Promise<void>
  lockToEmployeeId?: string
  absenceTypes: AbsenceTypeRecord[]
}

export function RegisterAbsenceDrawer({
  orgID: _orgID,
  onClose,
  onSave,
  lockToEmployeeId,
  absenceTypes,
}: RegisterAbsenceDrawerProps) {
  const { t } = useTranslation()
  const storeID: string = useAppSelector((state) => state?.auth?.currentStore?.storeID) || ''
  const [employeeId, setEmployeeId] = useState<string>(lockToEmployeeId ?? '')
  const [absenceTypeID, setAbsenceTypeID] = useState<string>('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [partial, setPartial] = useState(false)
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('17:00')
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: employees = [] } = useEmployees({
    params: { storeID },
    enabled: !lockToEmployeeId && !!storeID,
  })

  const requestedDays = useMemo(() => {
    if (!start) return 0
    const s = parseISO(start)
    const e = end ? parseISO(end) : s
    if (e < s) return 0
    return differenceInCalendarDays(e, s) + 1
  }, [start, end])

  const selectedType = absenceTypes.find((at) => at.absenceTypeID === absenceTypeID)
  const balance =
    selectedType?.annualLimitDays !== undefined && selectedType.annualLimitDays !== null
      ? { remaining: selectedType.annualLimitDays, total: selectedType.annualLimitDays }
      : null
  const remainingAfter = balance ? balance.remaining - requestedDays : null
  const insufficient = balance !== null && remainingAfter !== null && remainingAfter < 0

  const submit = async () => {
    if (!employeeId || !absenceTypeID || !start) {
      toast.error(t('leaves.register.missingFields'))
      return
    }
    if (insufficient) {
      toast.error(t('leaves.register.insufficientBalance'))
      return
    }
    const durationMinutes = requestedDays * STANDARD_WORKDAY_HOURS * 60
    setSaving(true)
    try {
      await onSave({
        absenceTypeID,
        employeeId,
        storeID,
        start,
        end: end || start,
        durationMinutes,
        comment,
        partial,
        startTime: partial ? startTime : undefined,
        endTime: partial ? endTime : undefined,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <SheetContent className="w-full sm:max-w-md p-0 flex flex-col">
      <SheetHeader className="px-6 py-4 border-b">
        <SheetTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-primary" /> {t('leaves.register.title')}
        </SheetTitle>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {!lockToEmployeeId && (
          <div className="space-y-2">
            <Label variant="eyebrow">
              {t('leaves.register.user')}
            </Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder={t('leaves.register.selectUser')} />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.employeeID} value={emp.employeeID}>
                    {emp.firstName} {emp.lastName}
                    {emp.shortUserName ? ` (${emp.shortUserName})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <Label variant="eyebrow">
            {t('leaves.register.absenceType')}
          </Label>
          <Select value={absenceTypeID} onValueChange={setAbsenceTypeID}>
            <SelectTrigger>
              <SelectValue placeholder={t('leaves.register.selectType')} />
            </SelectTrigger>
            <SelectContent>
              {absenceTypes.map((at) => (
                <SelectItem key={at.absenceTypeID} value={at.absenceTypeID}>
                  {at.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {absenceTypeID && (
            <div
              className={cn(
                'rounded-md border p-3 text-xs space-y-1.5',
                insufficient ? 'border-destructive/40 bg-destructive/5' : 'bg-muted/40',
              )}
            >
              {balance ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('leaves.register.available')}</span>
                    <span className="font-semibold">
                      {balance.remaining}
                      <span className="text-muted-foreground font-normal">
                        /{balance.total} {t('leaves.register.days')}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('leaves.register.requested')}</span>
                    <span className="font-semibold">
                      {requestedDays} {t('leaves.register.days')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-1.5 mt-1.5">
                    <span className="text-muted-foreground">
                      {t('leaves.balance.remainingDays', { days: '' })
                        .replace(/\s*\{?\{?days\}?\}?\s*/g, '')
                        .trim() || 'Remaining after'}
                    </span>
                    <span className={cn('font-semibold', insufficient && 'text-destructive')}>
                      {remainingAfter} {t('leaves.register.days')}
                    </span>
                  </div>
                  {insufficient && (
                    <p className="text-destructive pt-1">
                      {t('leaves.register.insufficientBalance')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">{t('leaves.register.noBalanceTracking')}</p>
              )}
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label variant="eyebrow">
            {t('leaves.register.dateRange')}
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('leaves.register.start')}</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t('leaves.register.end')}</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="flex items-start justify-between rounded-md border p-3">
          <div>
            <p className="text-sm font-medium">{t('leaves.register.partialDay')}</p>
            <p className="text-xs text-muted-foreground">{t('leaves.register.partialDayDesc')}</p>
          </div>
          <Switch checked={partial} onCheckedChange={setPartial} />
        </div>
        {partial && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('leaves.register.startTime')}</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t('leaves.register.endTime')}</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label variant="eyebrow">
            {t('leaves.register.comment')}
          </Label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('leaves.register.commentPlaceholder')}
            rows={4}
          />
        </div>
      </div>
      <SheetFooter className="px-6 py-4 border-t">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => void submit()} disabled={insufficient || saving}>
          <Plus className="w-4 h-4 mr-2" />
          {t('leaves.register.submit')}
        </Button>
      </SheetFooter>
    </SheetContent>
  )
}
